import { useState, useEffect, useRef } from "react";
import { SlidersHorizontal, Plus, Minus, Type, Eye, Zap, RotateCcw } from "lucide-react";

const FONT_SIZES = ["default", "lg", "xl", "xxl"];
const FONT_LABELS = { default: "100%", lg: "115%", xl: "125%", xxl: "140%" };

export default function AccessibilityToolbar() {
  const [open, setOpen] = useState(false);
  const [fontIdx, setFontIdx] = useState(0);
  const [highContrast, setHighContrast] = useState(false);
  const [dyslexia, setDyslexia] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const panelRef = useRef(null);

  // Load saved prefs
  useEffect(() => {
    const saved = localStorage.getItem("ada_prefs");
    if (saved) {
      try {
        const p = JSON.parse(saved);
        if (p.fontIdx) { setFontIdx(p.fontIdx); applyFont(p.fontIdx); }
        if (p.highContrast) { setHighContrast(true); document.documentElement.classList.add("ada-high-contrast"); }
        if (p.dyslexia) { setDyslexia(true); document.documentElement.classList.add("ada-dyslexia"); }
        if (p.reducedMotion) { setReducedMotion(true); document.documentElement.classList.add("ada-reduced-motion"); }
      } catch {}
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const save = (prefs) => localStorage.setItem("ada_prefs", JSON.stringify(prefs));

  const applyFont = (idx) => {
    FONT_SIZES.forEach(s => document.documentElement.classList.remove(`ada-font-${s}`));
    if (idx > 0) document.documentElement.classList.add(`ada-font-${FONT_SIZES[idx]}`);
  };

  const changeFontSize = (dir) => {
    const next = Math.max(0, Math.min(FONT_SIZES.length - 1, fontIdx + dir));
    setFontIdx(next);
    applyFont(next);
    save({ fontIdx: next, highContrast, dyslexia, reducedMotion });
  };

  const toggleHighContrast = () => {
    const v = !highContrast;
    setHighContrast(v);
    document.documentElement.classList.toggle("ada-high-contrast", v);
    save({ fontIdx, highContrast: v, dyslexia, reducedMotion });
  };

  const toggleDyslexia = () => {
    const v = !dyslexia;
    setDyslexia(v);
    document.documentElement.classList.toggle("ada-dyslexia", v);
    save({ fontIdx, highContrast, dyslexia: v, reducedMotion });
  };

  const toggleReducedMotion = () => {
    const v = !reducedMotion;
    setReducedMotion(v);
    document.documentElement.classList.toggle("ada-reduced-motion", v);
    save({ fontIdx, highContrast, dyslexia, reducedMotion: v });
  };

  const resetAll = () => {
    setFontIdx(0); setHighContrast(false); setDyslexia(false); setReducedMotion(false);
    applyFont(0);
    ["ada-high-contrast", "ada-dyslexia", "ada-reduced-motion"].forEach(c => document.documentElement.classList.remove(c));
    localStorage.removeItem("ada_prefs");
  };

  const activeCount = (fontIdx > 0 ? 1 : 0) + (highContrast ? 1 : 0) + (dyslexia ? 1 : 0) + (reducedMotion ? 1 : 0);

  return (
    <div ref={panelRef} className="relative" data-testid="ada-toolbar">
      <button
        onClick={() => setOpen(!open)}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
        aria-label="Accessibility options"
        data-testid="ada-toggle-btn"
      >
        <SlidersHorizontal className="w-4 h-4" />
        <span className="hidden sm:inline">ADA</span>
        {activeCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#1e3a5f] text-white text-[9px] font-bold flex items-center justify-center">{activeCount}</span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white border-2 border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden" role="dialog" aria-label="Accessibility Settings">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Accessibility</h3>
              <button onClick={resetAll} className="text-[11px] text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors" data-testid="ada-reset-btn">
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
            </div>
          </div>

          <div className="p-3 space-y-2">
            {/* Font Size */}
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50/80 border border-slate-100">
              <div className="flex items-center gap-2">
                <Type className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium text-slate-700">Text Size</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => changeFontSize(-1)} disabled={fontIdx === 0}
                  className="w-7 h-7 rounded-md flex items-center justify-center bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30 transition-colors" data-testid="ada-font-decrease">
                  <Minus className="w-3 h-3" />
                </button>
                <span className="w-10 text-center text-xs font-semibold text-slate-700">{FONT_LABELS[FONT_SIZES[fontIdx]]}</span>
                <button onClick={() => changeFontSize(1)} disabled={fontIdx === FONT_SIZES.length - 1}
                  className="w-7 h-7 rounded-md flex items-center justify-center bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30 transition-colors" data-testid="ada-font-increase">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* High Contrast */}
            <button onClick={toggleHighContrast}
              className={`w-full flex items-center justify-between p-2.5 rounded-lg border transition-colors ${highContrast ? "bg-[#1e3a5f]/5 border-[#1e3a5f]/30" : "bg-slate-50/80 border-slate-100 hover:bg-slate-100"}`}
              data-testid="ada-contrast-toggle">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium text-slate-700">High Contrast</span>
              </div>
              <div className={`w-9 h-5 rounded-full transition-colors relative ${highContrast ? "bg-[#1e3a5f]" : "bg-slate-300"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${highContrast ? "translate-x-4" : "translate-x-0.5"}`} />
              </div>
            </button>

            {/* Dyslexia Font */}
            <button onClick={toggleDyslexia}
              className={`w-full flex items-center justify-between p-2.5 rounded-lg border transition-colors ${dyslexia ? "bg-[#1e3a5f]/5 border-[#1e3a5f]/30" : "bg-slate-50/80 border-slate-100 hover:bg-slate-100"}`}
              data-testid="ada-dyslexia-toggle">
              <div className="flex items-center gap-2">
                <Type className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium text-slate-700">Dyslexia Font</span>
              </div>
              <div className={`w-9 h-5 rounded-full transition-colors relative ${dyslexia ? "bg-[#1e3a5f]" : "bg-slate-300"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${dyslexia ? "translate-x-4" : "translate-x-0.5"}`} />
              </div>
            </button>

            {/* Reduced Motion */}
            <button onClick={toggleReducedMotion}
              className={`w-full flex items-center justify-between p-2.5 rounded-lg border transition-colors ${reducedMotion ? "bg-[#1e3a5f]/5 border-[#1e3a5f]/30" : "bg-slate-50/80 border-slate-100 hover:bg-slate-100"}`}
              data-testid="ada-motion-toggle">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium text-slate-700">Reduce Motion</span>
              </div>
              <div className={`w-9 h-5 rounded-full transition-colors relative ${reducedMotion ? "bg-[#1e3a5f]" : "bg-slate-300"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${reducedMotion ? "translate-x-4" : "translate-x-0.5"}`} />
              </div>
            </button>
          </div>

          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50">
            <p className="text-[10px] text-slate-400 text-center">WCAG 2.1 AA Compliant</p>
          </div>
        </div>
      )}
    </div>
  );
}
